import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import PulseLogo from "./PulseLogo";
import Sidebar from "./Sidebar";
import AICopilotRail from "./AICopilotRail";
import ActivityTicker from "./ActivityTicker";
import NoiseOverlay from "./NoiseOverlay";
import MorningBriefing from "./MorningBriefing";
import LiveNudges from "./LiveNudges";
import CommandPalette from "./CommandPalette";
import { usePageTracking } from "../lib/useTracking";
import { getDemoHeadline, getPurchaseRecommendations } from "../lib/api";

// Responsive widths: scale with viewport but clamp to readable bounds
const SIDEBAR_W = "clamp(220px, 15vw, 280px)";
const RAIL_W = "clamp(280px, 19vw, 340px)";

export default function Layout() {
  usePageTracking();
  const location = useLocation();
  const [mobileNav, setMobileNav] = useState(false);
  const [headline, setHeadline] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const scrollRef = useRef(null);

  // Disable browser's automatic scroll restoration — the scroll lives inside
  // an inner div, not <body>, so the browser cannot restore it correctly anyway.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // On any route change (and on initial mount), reset the inner scroll
  // container AND the window to the top. This makes Cmd/Ctrl+R and normal
  // navigation behave the same: page always opens from the top.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    setMobileNav(false);
  }, [location.pathname]);

  // Fetch shared data once for LiveNudges
  useEffect(() => {
    Promise.all([getDemoHeadline(), getPurchaseRecommendations(4)])
      .then(([h, r]) => {
        setHeadline(h.data);
        setRecommendations(r.data || []);
      })
      .catch(() => { /* fail silent */ });
  }, []);

  return (
    <div className="h-screen w-full bg-obsidian text-foreground overflow-hidden relative flex" dir="rtl">
      <NoiseOverlay />
      <MorningBriefing />
      <LiveNudges headline={headline} recommendations={recommendations} hidden />
      <CommandPalette />

      {/* Right sidebar — first in DOM, visually right because of RTL */}
      <aside
        className="hidden lg:block shrink-0 bg-[#0A0A0E]"
        style={{ width: SIDEBAR_W }}
        data-testid="sidebar-desktop"
      >
        <Sidebar />
      </aside>

      {/* Main content — flex-1 takes remaining width */}
      <main
        className="flex-1 min-w-0 h-screen flex flex-col pt-14 lg:pt-0"
        data-testid="main-content"
      >
        <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden" data-testid="main-scroll">
          <Outlet />
        </div>
        <ActivityTicker />
      </main>

      {/* Left AI Copilot rail — XL+ only, hidden on /chat */}
      {!location.pathname.startsWith("/chat") && (
        <aside
          className="hidden xl:block shrink-0 bg-[#0A0A0E]"
          style={{ width: RAIL_W }}
          data-testid="ai-copilot-aside"
        >
          <AICopilotRail embedded />
        </aside>
      )}

      {/* AI Copilot FAB / sheet for <xl */}
      <AICopilotRail fab />

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 right-0 left-0 z-30 bg-[#0A0A0E] border-b border-border/40 px-4 py-3 flex items-center justify-between" dir="ltr">
        <div className="flex flex-col items-start leading-none">
          <span className="text-[15px] font-bold tracking-tight text-foreground" style={{ letterSpacing: "-0.02em" }}>
            Opty<span className="text-primary">go</span>
            <span className="text-zinc-300 font-medium ms-1">Pulse</span>
          </span>
          <span className="text-[8px] text-zinc-500 font-mono tracking-[0.18em] uppercase mt-1">
            Smart Business Insights
          </span>
        </div>
        <button
          onClick={() => setMobileNav(true)}
          className="p-2 text-foreground"
          data-testid="mobile-menu-btn"
          aria-label="פתח תפריט"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileNav && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
              onClick={() => setMobileNav(false)}
              data-testid="mobile-overlay"
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="lg:hidden fixed top-0 right-0 bottom-0 z-50 w-72"
              data-testid="mobile-sidebar"
            >
              <div className="relative h-full bg-[#0A0A0E]">
                <button
                  onClick={() => setMobileNav(false)}
                  className="absolute top-4 left-4 z-10 text-zinc-500 p-1"
                  aria-label="סגור"
                >
                  <X className="w-5 h-5" />
                </button>
                <Sidebar onNavigate={() => setMobileNav(false)} />
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
